import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Badge,
} from "reactstrap";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import SocketEngine from "../../core/SocketEngine";
import { connect } from "react-redux";
import loadingAction from "../../core/redux/actions/loadingAction";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import strings from "../../core/translate";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import RowActions, {
  
} from "components/Table/RowActions";
import SelectRow from "../../components/Select/SelectRow";
import { statusLabel, statusTone } from "../../shared/work-orders";
import "../../assets/css/work-orders.css";

// hours remaining until delivery; negative means overdue
const hoursUntil = (deliveryAt) =>
  (new Date(deliveryAt).getTime() - Date.now()) / (60 * 60 * 1000);

// most-urgent-first: red wins over yellow, yellow wins over green
const deliveryColorFor = (deliveryAt, setup) => {
  if (!deliveryAt || !setup) return null;
  const hours = hoursUntil(deliveryAt);
  if (hours <= setup.redThresholdHours) return "danger";
  if (hours <= setup.yellowThresholdHours) return "warning";
  if (hours <= setup.greenThresholdHours) return "success";
  return null;
};

class WorkOrderList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      dataSource: [],
      merchants: [],
      branches: [],
      branchName: "",
      garageScoped: false,
      appointments: [],
      selectedMerchant: "",
      selectedBranch: "",
      selectedAppointment: "",
      garageSetup: null,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");
    const { selectedBranch } = this.props.match.params;
    const routeState = this.props.location.state || {};

    // Subscribed first: a garage login returns early below, and used to leave
    // this list without the live updates every other login got.
    SocketEngine.on("work_order:updated", this.handleWorkOrderUpdated);
    SocketEngine.on("booking:updated", this.handleWorkOrderUpdated);
    SocketEngine.on("job_card:updated", this.handleWorkOrderUpdated);

    // reached from a garage record: this page is about that garage only
    if (selectedBranch && selectedBranch !== ":selectedBranch") {
      this.setState(
        {
          selectedBranch,
          garageScoped: true,
          branchName: routeState.branchName || "",
          selectedMerchant:
            merchantIdFromStorage || routeState.selectedMerchant || "",
        },
        () => {
          this.fetchAppointments();
          this.fetchGarageSetup();
          this.callPage();
        }
      );
      return;
    }

    if (merchantIdFromStorage) {
      this.setState({ selectedMerchant: merchantIdFromStorage }, this.fetchBranches);
    } else {
      this.fetchMerchants();
    }
  }

  componentWillUnmount() {
    SocketEngine.off("work_order:updated", this.handleWorkOrderUpdated);
    SocketEngine.off("booking:updated", this.handleWorkOrderUpdated);
    SocketEngine.off("job_card:updated", this.handleWorkOrderUpdated);
  }

  // Live update: a work order, its booking, or a walk-in job card changed —
  // refresh the list if it's for the branch currently on screen.
  handleWorkOrderUpdated = (payload) => {
    if (!this.state.selectedBranch) return;
    if (payload?.branchId && payload.branchId !== this.state.selectedBranch) {
      return;
    }
    this.callPage();
  };

  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      const merchants = response.data.items.map((m) => ({ value: m.id, label: m.name }));
      this.setState({ merchants });
      if (merchants.length === 1) {
        this.setState({ selectedMerchant: merchants[0].value }, this.fetchBranches);
      }
    }
  };

  fetchBranches = async () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant) return;
    const response = await this.engine.getItem(`merchants/${selectedMerchant}/branches`);
    if (response && response.status === 200) {
      const branches = response.data.items.map((b) => ({ value: b.id, label: b.name }));
      this.setState({ branches, appointments: [], dataSource: [] });
      if (branches.length === 1) {
        this.setState({ selectedBranch: branches[0].value }, () => {
          this.fetchAppointments();
          this.fetchGarageSetup();
          this.callPage();
        });
      }
    }
  };

  fetchAppointments = async () => {
    const { selectedBranch } = this.state;
    if (!selectedBranch) return;
    const response = await this.engine.getItem(
      `merchants-branches/${selectedBranch}/appointments`
    );
    if (response && response.status === 200) {
      const appointments = [
        { value: "", label: "All Appointments" },
        ...response.data.items.map((a) => ({
          value: a.id,
          label: `${a.date} ${a.fromToHours}`,
        })),
      ];
      this.setState({ appointments });
    }
  };

  // drives the delivery-date traffic-light column below
  fetchGarageSetup = async () => {
    const { selectedBranch } = this.state;
    if (!selectedBranch) return;
    const response = await this.engine.getItem(
      `merchants-branches/${selectedBranch}/garage-setup`
    );
    if (response && response.status === 200) {
      const items = response.data.items || [];
      this.setState({ garageSetup: items[0] || null });
    }
  };

  // Load all work orders for the branch, then optionally filter by appointment
  callPage = async () => {
    const { selectedBranch, selectedAppointment } = this.state;
    if (!selectedBranch) return;

    this.setState({ isLoading: true });

    let rows = [];

    if (selectedAppointment) {
      // ── appointment selected: fetch its bookings and their work orders ──
      const bookingsRes = await this.engine.getItem(
        `merchants-branches/${selectedAppointment}/appointment-booking`
      );
      if (bookingsRes && bookingsRes.status === 200) {
        const bookings = bookingsRes.data?.items ?? [];
        for (const booking of bookings) {
          try {
            const woRes = await this.engine.getItem(`bookings/${booking.id}/work-order`);
            if (woRes && woRes.status === 200) {
              const wo = woRes.data;
              const appt = booking.appointment ?? {};
              rows.push(
                this._buildRow(wo, booking, appt.date ?? "", appt.fromToHours ?? "")
              );
            }
          } catch (_) {}
        }
      }
    } else {
      // ── no appointment filter: fetch all work orders for the branch ──
      const res = await this.engine.getItem(
        `merchants-branches/${selectedBranch}/work-orders`
      );
      if (res && res.status === 200) {
        rows = (res.data ?? []).map((wo) => ({
          bookingId: wo.bookingId,
          workOrderId: wo.id,
          customer: wo.customer,
          vehicle: `${wo.vehicleMake ?? ""} ${wo.vehicleModel ?? ""}`.trim() || "—",
          itemsCount: wo.itemsCount,
          approvedCount: wo.approvedCount,
          status: wo.status,
          appointmentDate: wo.appointmentDate,
          appointmentTime: wo.appointmentTime,
          deliveryAt: wo.deliveryAt ?? null,
          createdAt: new Date(wo.createdAt),
          actions: this.handleActions({
            bookingId: wo.bookingId,
            workOrderId: wo.id,
            customer: wo.customer,
            vehicle: `${wo.vehicleMake ?? ""} ${wo.vehicleModel ?? ""}`.trim() || "—",
          }),
        }));
      }
    }

    this.setState({ dataSource: rows, isLoading: false });
    this.props.loadingAction(false);
  };

  _buildRow = (wo, booking, appointmentDate, appointmentTime) => {
    const customer = booking.customer?.user
      ? `${booking.customer.user.firstName ?? ""} ${booking.customer.user.lastName ?? ""}`.trim()
      : booking.customer?.id ?? "—";
    const vehicle = booking.customerVehicle
      ? `${booking.customerVehicle.make?.name ?? ""} ${booking.customerVehicle.model?.name ?? ""}`.trim()
      : "—";
    const ref = { bookingId: booking.id, workOrderId: wo.id, customer, vehicle };
    return {
      bookingId: booking.id,
      workOrderId: wo.id,
      customer,
      vehicle,
      itemsCount: wo.items?.length ?? 0,
      approvedCount: (wo.items ?? []).filter((i) => i.status === "approved").length,
      status: wo.status,
      appointmentDate,
      appointmentTime,
      deliveryAt: wo.deliveryAt ?? null,
      createdAt: new Date(wo.createdAt),
      actions: this.handleActions(ref),
    };
  };

  handleManage = (ref) => {
    this.props.history.push({
      pathname: `/admin/work-orders/${ref.workOrderId}/manage`,
      state: {
        workOrder: { id: ref.workOrderId },
        booking: ref.bookingId ? { id: ref.bookingId } : null,
        meta: { customer: ref.customer, vehicle: ref.vehicle },
      },
    });
  };

  handleActions = (ref) => (
    <RowActions
      actions={[
        {
          key: "manage",
          icon: "fa fa-wrench",
          label: strings.manage,
          // the only thing a work order row is for, so it says so
          primary: true,
          onClick: () => this.handleManage(ref),
        },
      ]}
    />
  );

  render() {
    const {
      dataSource, isLoading, merchants, branches, appointments,
      selectedMerchant, selectedBranch, selectedAppointment,
      garageScoped, branchName, garageSetup,
    } = this.state;
    const showMerchantSelect =
      !sessionStorage.getItem("merchantId") && merchants.length !== 1;

    const columns = [
      { Header: "Customer", accessor: "customer" },
      { Header: "Vehicle", accessor: "vehicle" },
      {
        Header: "Appointment",
        id: "appointment",
        accessor: (r) =>
          [r.appointmentDate, r.appointmentTime].filter(Boolean).join(" ") || "—",
        filterable: false,
      },
      {
        Header: "Items",
        accessor: "itemsCount",
        Cell: (row) => `${row.original.approvedCount}/${row.value} approved`,
        filterable: false,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (row) =>
          row.value ? (
            <span className={`wo-pill is-${statusTone(row.value)}`}>
              {statusLabel(row.value)}
            </span>
          ) : (
            <span className="wo-pill is-muted">{strings.no_work_order}</span>
          ),
        filterable: false,
      },
      {
        Header: strings.delivery,
        accessor: "deliveryAt",
        Cell: (row) => {
          if (!row.value) return "—";
          const color = deliveryColorFor(row.value, garageSetup);
          const label = new Date(row.value).toLocaleString();
          return color ? <Badge color={color}>{label}</Badge> : label;
        },
        filterable: false,
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: (row) => row.value.toLocaleDateString(),
        filterable: false,
      },
      {
        Header: strings.actions,
        accessor: "actions",
        sortable: false,
        filterable: false,
      },
    ];

    return (
      <div className="content">
        <GarageSectionHeader
          branchId={garageScoped ? selectedBranch : ""}
          branchName={branchName}
          merchantId={selectedMerchant}
          active="work-orders"
          history={this.props.history}
        />
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <h5 className="mb-2">{strings.work_orders}</h5>
                <Row>
                  {!garageScoped && showMerchantSelect && (
                    <Col md="3">
                      <SelectRow
                        label={strings.merchant}
                        name="selectedMerchant"
                        defaultValue={selectedMerchant}
                        data={merchants}
                        changeInput={(e) =>
                          this.setState(
                            {
                              selectedMerchant: e.value,
                              selectedBranch: "",
                              selectedAppointment: "",
                              dataSource: [],
                            },
                            this.fetchBranches
                          )
                        }
                        placeholder={strings.select}
                      />
                    </Col>
                  )}
                  {!garageScoped && branches.length > 0 && (
                    <Col md="3">
                      <SelectRow
                        label={strings.garage}
                        name="selectedBranch"
                        defaultValue={selectedBranch}
                        data={branches}
                        changeInput={(e) =>
                          this.setState(
                            {
                              selectedBranch: e.value,
                              selectedAppointment: "",
                              dataSource: [],
                            },
                            () => {
                              this.fetchAppointments();
                              this.fetchGarageSetup();
                              this.callPage();
                            }
                          )
                        }
                        placeholder="Select Branch"
                      />
                    </Col>
                  )}
                  {selectedBranch && appointments.length > 0 && (
                    <Col md="4">
                      <SelectRow
                        label="Appointment Slot (optional)"
                        name="selectedAppointment"
                        defaultValue={selectedAppointment}
                        data={appointments}
                        changeInput={(e) =>
                          this.setState(
                            { selectedAppointment: e.value },
                            this.callPage
                          )
                        }
                        placeholder="All Appointments"
                      />
                    </Col>
                  )}
                </Row>
              </CardHeader>
              <CardBody>
                {!selectedBranch ? (
                  <p className="text-muted text-center py-4">
                    Select a branch to view its work orders.
                  </p>
                ) : (
                  <ReactTable
                    previousText={strings.previous}
                    nextText={strings.next}
                    loadingText={strings.loading}
                    noDataText={strings.nodata}
                    pageText={strings.page}
                    data={dataSource}
                    loading={isLoading}
                    columns={columns}
                    defaultPageSize={10}
                    filterable
                    showPaginationTop={false}
                    showPaginationBottom
                    PaginationComponent={TablePagination}
                    className="-striped -highlight primary-pagination"
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(WorkOrderList);
